/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {MutationTree} from 'vuex';
import {DetailsStageInterface, FormStateInterface, ReportStageInterface} from '@/store/modules/form/types';
import {Vue} from 'vue-property-decorator';

export const mutations: MutationTree<FormStateInterface> = {

    /**
     * Cancel the reporting of an issue.
     * @param {FormStateInterface} state
     */
    cancel(state: FormStateInterface): void {
        Vue.set(state, 'stage', 1);
        Vue.set(state, 'reportStage', {});
        Vue.set(state, 'detailsStage', {problem: {}, hardware: {}});
    },

    /**
     * Update the stage state.
     *
     * @param {FormStateInterface} state
     * @param {number} newStage
     */
    updateStage(state: FormStateInterface, newStage: number): void {
        Vue.set(state, 'stage', newStage);
    },

    /**
     * Update the stage state.
     *
     * @param state
     * @param gameSelected
     */
    updateGameSelected(state: FormStateInterface, gameSelected: number): void {
        Vue.set(state.reportStage, 'game', gameSelected);
    },


    /******************************
     * Report stage 1. Report
     ******************************/

    /**
     * Update the Report stage state 1.Report.
     *
     * @param {FormStateInterface} state
     * @param {ReportStageInterface} newReportStage
     */
    updateReportStage(state: FormStateInterface, newReportStage: ReportStageInterface): void {
        Vue.set(state, 'reportStage', {...state.reportStage, ...newReportStage});
    },

    /**
     * Update the Report stage state 1.Report problem field.
     *
     * @param {FormStateInterface} state
     * @param {string} newProblemTitle
     */
    updateProblemTitle(state: FormStateInterface, newProblemTitle: string): void {
        Vue.set(state.reportStage, 'problemTitle', newProblemTitle);
    },

    /**
     * Update the Report stage state 1.Report selected game field.
     *
     * @param {FormStateInterface} state
     * @param {string} newGame
     */
    updateGame(state: FormStateInterface, newGame: string): void {
        Vue.set(state.reportStage, 'game', newGame);
    },

    /**
     * Update the Report stage state 1.Report selected platform field.
     *
     * @param {FormStateInterface} state
     * @param {{id: number, name: string}} newPlatform
     */
    updatePlatform(state: FormStateInterface, newPlatform: {id: number, name: string}): void {
        Vue.set(state.reportStage, 'platform', newPlatform);
    },

    /**
     * Update the Report stage state 1.Report Category field.
     *
     * @param {FormStateInterface} state
     * @param {string} newCategory
     */
    updateCategory(state: FormStateInterface, newCategory: string): void {
        Vue.set(state.reportStage, 'category', newCategory);
    },


    /******************************
     * Report stage 3. Details
     ******************************/

    /**
     * Update the Report stage state 3. Details.
     *
     * @param {FormStateInterface} state
     * @param {DetailsStageInterface} newDetailsStage
     */
    updateDetailsStage(state: FormStateInterface, newDetailsStage: DetailsStageInterface): void {
        Vue.set(state, 'detailsStage', newDetailsStage);
    },

    /**
     * Update the Report stage state 3. Details problem description field.
     *
     * @param {FormStateInterface} state
     * @param {string} newProblemDescription
     */
    updateProblemDescription(state: FormStateInterface, newProblemDescription: string): void {
        Vue.set(state.detailsStage.problem, 'problemDescription', newProblemDescription);
    },

    /**
     * Update the Report stage state 3. Details reproduction steps field.
     *
     * @param {FormStateInterface} state
     * @param {string} newReproductionSteps
     */
    updateReproductionSteps(state: FormStateInterface, newReproductionSteps: string): void {
        Vue.set(state.detailsStage.problem, 'reproductionSteps', newReproductionSteps);
    },

    /**
     * Update the Report stage state 3. Details can reproduce.
     *
     * @param {FormStateInterface} state
     * @param {boolean} newCanReproduce
     */
    updateCanReproduce(state: FormStateInterface, newCanReproduce: boolean): void {
        Vue.set(state.detailsStage.problem, 'canReproduce', newCanReproduce);
    },

    /**
     * Update the Report stage state 3. Details Not a Bug.
     *
     * @param {FormStateInterface} state
     * @param {boolean} newNotBug
     */
    updateNotBug(state: FormStateInterface, newNotBug: boolean): void {
        Vue.set(state.detailsStage.problem, 'notBug', newNotBug);
    },

    /**
     * Update the Report stage state 3. Details Region field.
     *
     * @param {FormStateInterface} state
     * @param {string} newRegion
     */
    updateRegion(state: FormStateInterface, newRegion: string): void {
        Vue.set(state.detailsStage.hardware, 'region', newRegion);
    },

    /**
     * Update the Report stage state 3. Details Internet Provider.
     *
     * @param {FormStateInterface} state
     * @param {string} newInternetProvider
     */
    updateInternetProvider(state: FormStateInterface, newInternetProvider: string): void {
        Vue.set(state.detailsStage.hardware, 'internetProvider', newInternetProvider);
    },

    /**
     * Update the Report stage state 3. Details Internet Speed.
     *
     * @param {FormStateInterface} state
     * @param {string} newInternetSpeed
     */
    updateInternetSpeed(state: FormStateInterface, newInternetSpeed: string): void {
        Vue.set(state.detailsStage.hardware, 'internetSpeed', newInternetSpeed);
    },

    /**
     * Update the Report stage state 3. Details Additional Technical Info.
     *
     * @param {FormStateInterface} state
     * @param {string} newAdditionalTechnicalInfo
     */
    updateAdditionalTechnicalInfo(state: FormStateInterface, newAdditionalTechnicalInfo: string): void {
        Vue.set(state.detailsStage.hardware, 'additionalTechnicalInfo', newAdditionalTechnicalInfo);
    },

    /**
     * Update the Report stage state 3. Details Operating System.
     *
     * @param {FormStateInterface} state
     * @param {string} newOS
     */
    updateOS(state: FormStateInterface, newOS: string): void {
        Vue.set(state.detailsStage.hardware, 'os', newOS);
    },

    /**
     * Update the Report stage state 3. Details Operating System for PS4.
     *
     * @param {FormStateInterface} state
     * @param {string} newOS
     */
    updateOSPS4(state: FormStateInterface, newOS: string): void {
        Vue.set(state.detailsStage.hardware, 'osPS4', newOS);
    },

    /**
     * Update the Report stage state 3. Details Graphic Card.
     *
     * @param {FormStateInterface} state
     * @param {string} newGraphicCard
     */
    updateGraphicCard(state: FormStateInterface, newGraphicCard: string): void {
        Vue.set(state.detailsStage.hardware, 'graphicCard', newGraphicCard);
    },

    /**
     * Update the Report stage state 3. Details System Memory.
     *
     * @param {FormStateInterface} state
     * @param {string} newSystemMemory
     */
    updateSystemMemory(state: FormStateInterface, newSystemMemory: string): void {
        Vue.set(state.detailsStage.hardware, 'systemMemory', newSystemMemory);
    },

    /**
     * Update the Report stage state 3. Details Processor.
     *
     * @param {FormStateInterface} state
     * @param {string} newProcessor
     */
    updateProcessor(state: FormStateInterface, newProcessor: string): void {
        Vue.set(state.detailsStage.hardware, 'processor', newProcessor);
    },

    /**
     * Update the Report stage state 3. Details DirectX.
     *
     * @param {FormStateInterface} state
     * @param {string} newDirectX
     */
    updateDirectX(state: FormStateInterface, newDirectX: string): void {
        Vue.set(state.detailsStage.hardware, 'directVersion', newDirectX);
    },

    /**
     * Update the Report stage state 3. Details Times seen.
     *
     * @param {FormStateInterface} state
     * @param {number} newTimesSeen
     */
    updateTimesSeen(state: FormStateInterface, newTimesSeen: number): void {
        Vue.set(state.detailsStage.problem, 'timesSeen', newTimesSeen);
    },

    /**
     * Update the Report stage state 3. Details Platform Type
     *
     * @param {FormStateInterface} state
     * @param {{id: string, name: string}} newPlatformType
     */
    updatePlatformType(state: FormStateInterface, newPlatformType: {id: string, name: string}): void {
        Vue.set(state.detailsStage.hardware, 'type', newPlatformType);
    },

    /**
     * Delete the Report stage state 3. Details Platform Type
     *
     * @param {FormStateInterface} state
     */
    deletePlatformType(state: FormStateInterface): void {
        Vue.delete(state.detailsStage.hardware, 'type');
    },

    /**
     * Delete the Report stage state 3. Details additional PC fields
     *
     * @param {FormStateInterface} state
     */
    deletePCFields(state: FormStateInterface): void {
        Vue.delete(state.detailsStage.hardware, 'os');
        Vue.delete(state.detailsStage.hardware, 'processor');
        Vue.delete(state.detailsStage.hardware, 'systemMemory');
        Vue.delete(state.detailsStage.hardware, 'graphicCard');
        Vue.delete(state.detailsStage.hardware, 'directVersion');
    },

    /**
     * Delete the Report stage state 3. Details additional PS4 fields
     *
     * @param {FormStateInterface} state
     */
    deletePS4Fields(state: FormStateInterface): void {
        Vue.delete(state.detailsStage.hardware, 'osPS4');
    },


    /**
     * subscription for issue
     *
     * @param {FormStateInterface} state
     * @param {boolean} subscription
     */
    subscribeIssue(state: FormStateInterface, subscription: boolean): void {
        Vue.set(state.detailsStage, 'subscription', subscription);
    },
};
